import React from "react";
import DataTable from "../DataTable";
import { useAuthState } from "../../../components/AuthProvider";
import ColumnSelector from "./ColumnSelector";
import { records as fhirToDatatable } from "../../../utils/fhirToDatatable";
import formattedDate, {
  formattedDateWithDefault,
  formattedTimeStampWithDefault,
} from "../../../utils/formattedDate";
import { identifiersConfig } from "../../../utils/identifiersConfig";
import Container from "../../../components/Container";

import styles from "./RecordHistory.module.scss";
import Tooltip from "../../../components/Tooltip";
import Content from "../../../components/Content";

const RecordHistory = ({
  history = null,
  currentRecord,
  isNewQuarantine = false,
  entityId = "",
  sourceId = "",
  nameToTypeMap,
}) => {
  const { user } = useAuthState();
  if (currentRecord && currentRecord.eimpLastUpdated && !isNewQuarantine) {
    const dateObject = new Date(currentRecord.eimpLastUpdated);
    currentRecord.lastUpdatedTime = dateObject.getTime();
  }
  let historyWithCurrent = "";
  let records = [currentRecord, ...(history || [])];
  historyWithCurrent = records.filter(
    (record) => record !== null && record !== undefined,
  );
  const sortHistory = historyWithCurrent.sort(function (a, b) {
    return b.lastUpdatedTime - a.lastUpdatedTime;
  });
  const lastUpdatedCell = ({ row }) => {
    if (row.original.lastupdated === "Current") return row.original.lastupdated;
    return row.original.lastupdated
      ? formattedDateWithDefault(row.original.lastupdated)
      : "-";
  };
  const lastUpdatedTimeCell = ({ row }) => {
    if (row.original.lastUpdatedTime === "Current") {
      return row.original.eimpLastUpdated
        ? formattedDateWithDefault(row.original.eimpLastUpdated)
        : "-";
    }
    return row.original.lastUpdatedTime
      ? formattedTimeStampWithDefault(row.original.lastUpdatedTime)
      : "-";
  };
  if (sortHistory)
    sortHistory.forEach(
      (historyRecord) => (historyRecord.table = "historyTable"),
    );
  if (currentRecord) {
    currentRecord.table = "historyTable";
  }
  const sortedFhirRecords = sortHistory;
  const data = sortHistory
    ? fhirToDatatable(sortHistory, user.preferences.sources, nameToTypeMap)
    : [];
  if (data) {
    const result = data.find((item) => item.isQuarantine === false);
    if (result !== undefined) {
      result.lastUpdatedTime = "Current";
    }
    if (isNewQuarantine) {
      sortedFhirRecords.shift();
      data.shift();
    }

    for (const row of data) {
      if (row.isQuarantine === true) {
        row.undoQuarantine = true;
        break;
      }
    }
  }
  // data[0].lastUpdatedTime = "Current";

  const [hiddenColumns, setHiddenColumns] = React.useState([
    "mrn",
    "ssn",
    "isQuarantine",
  ]);
  const [columnOrder, setColumnOrder] = React.useState([]);
  const identifierColumns = [];
  identifiersConfig.forEach((identifier) => {
    identifierColumns.push({
      Header: identifier.heading,
      accessor: identifier.heading.toLowerCase(),
    });
  });

  const columns = [
    {
      Header: "Last Updated By Source",
      accessor: "lastupdated",
      Cell: lastUpdatedCell,
    },
    {
      Header: "Last Updated Time",
      accessor: "lastUpdatedTime",
      Cell: lastUpdatedTimeCell,
    },
    {
      Header: "First Name",
      accessor: "givenName",
    },
    {
      Header: "Last Name",
      accessor: "familyName",
    },
    {
      Header: "Quarantine Type",
      accessor: "quarantineType",
    },
    {
      Header: "Quarantine Reason",
      accessor: "quarantineReason",
    },
    {
      Header: "Date Of Birth",
      accessor: "birthDate",
    },
    {
      Header: "Gender",
      accessor: "gender",
    },
    {
      Header: "Address Line",
      accessor: "addressLine",
    },
    {
      Header: "City",
      accessor: "city",
    },
    {
      Header: "Postal Code",
      accessor: "postalCode",
    },
    {
      Header: "Phone",
      accessor: "phone",
    },
    {
      Header: "Email",
      accessor: "email",
    },
    {
      Header: "Golden ID",
      accessor: "linkReferenceId",
    },
    {
      Header: "Is Quarantine",
      accessor: "isQuarantine",
    },
    {
      Header: "Action",
      accessor: "action",
    },
    {
      Header: "Done By",
      accessor: "overriddenBy",
    },
    {
      Header: "Reason",
      accessor: "overrideReason",
      Cell: ({ value }) => (
        <div style={{ minWidth: 300, whiteSpace: "normal" }}>{value}</div>
      ),
    },
    ...identifierColumns,
  ];

  // Column Selector Props

  const shuffleColumns = (hiddenColumnsSet, order) => {
    const orderedColumns = [];
    orderedColumns.push("lastUpdatedTime");
    orderedColumns.push("lastupdated");
    orderedColumns.push("quarantineType");
    orderedColumns.push("quarantineReason");
    order.forEach((category) => {
      category.fields.forEach((field) => {
        orderedColumns.push(field);
      });
    });
    setColumnOrder(orderedColumns);
    setHiddenColumns([...hiddenColumnsSet]);
  };

  const staticFields = ["lastupdated", "lastUpdatedTime", "hrn"];
  const defaultSelectedFields = [
    "givenName",
    "familyName",
    "birthDate",
    "gender",
    "addressLine",
    "city",
    "postalCode",
    "phone",
    "email",
    "linkReferenceId",
    "quarantineType",
    "quarantineReason",
    "action",
    "overriddenBy",
    "overrideReason",
    "recordHistoryActions",
  ];

  const defaultColumnOrder = [
    {
      heading: "Name",
      fields: ["givenName", "familyName"],
    },
    {
      heading: "ID",
      fields: [
        ...identifiersConfig.map((identifier) =>
          identifier.heading.toLowerCase(),
        ),
      ],
    },
    {
      heading: "Address",
      fields: ["addressLine", "city", "postalCode"],
    },
    {
      heading: "Demographics",
      fields: ["birthDate", "deceasedDateTime", "gender"],
    },
    {
      heading: "Contact",
      fields: ["phone", "email"],
    },
    {
      heading: "Golden ID",
      fields: ["linkReferenceId"],
    },
  ];

  let historyDescription;
  if (isNewQuarantine) {
    historyDescription = (
      <p>
        Below is the history of the quarantined record. Note that <br />
        since this record was not part of any aggregate there <br />
        are no aggregate details on this page. You can undo the <br />
        latest quarantine via the Undo Quarantine button.
      </p>
    );
  } else {
    historyDescription = (
      <p>
        Below is the record history section. Rows can be selected <br /> to view
        the full details of the version. Rows highlighted <br /> in red are
        quarantined records. If the latest record is <br /> quarantined, you can
        undo the quarantine via the Undo <br /> Quarantine button.
      </p>
    );
  }

  return (
    <Container className={styles.base}>
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Tooltip recordHistory={true}>
          <Content>{historyDescription}</Content>
        </Tooltip>
        <ColumnSelector
          name="recordHistory"
          columnsPrimary={columns}
          staticFields={staticFields}
          defaultSelectedFields={defaultSelectedFields}
          defaultColumnOrder={defaultColumnOrder}
          onApply={shuffleColumns}
          popoverClasses={{ right: "200%" }}
        />
      </span>

      <DataTable
        columns={columns}
        hiddenColumns={hiddenColumns}
        columnOrder={columnOrder}
        data={data}
        hasHover={false}
        isStripped={false}
        entityId={entityId}
        sourceId={sourceId}
        getHeaderProps={() => ({
          style: {
            backgroundColor: "#fff",
          },
        })}
        getCellProps={({ value, ...cell }) => {
          let backgroundColor = null;
          let rowId = +cell.row.id;
          if (
            cell.column.id !== "lastupdated" &&
            cell.column.id !== "lastUpdatedTime" &&
            rowId !== data.length - 1
          ) {
            const key = cell.column.id;
            const nextRow = data[rowId + 1];
            const cellValue = Array.isArray(value) ? value.join(", ") : value;
            const nextRowCellValue = Array.isArray(nextRow[key])
              ? nextRow[key].join(", ")
              : nextRow[key];

            if (cell.row.values.isQuarantine) {
              backgroundColor = "#ff9d97";
            }

            if (nextRowCellValue !== cellValue && key != "quarantineType") {
              if (cell.row.values.isQuarantine) {
                backgroundColor = "#FF827D";
              } else {
                backgroundColor = "#e6d3a9";
              }
            }
          } else {
            if (cell.row.values.isQuarantine) {
              backgroundColor = "#ff9d97";
            }
          }
          return {
            style: { backgroundColor: backgroundColor },
          };
        }}
        FHIRRecordList={sortedFhirRecords}
      />
    </Container>
  );
};

export default RecordHistory;
